import React from 'react';
import { Link } from 'react-router-dom';

/**
 * 상품 관련 페이지의 공통 헤더 컴포넌트
 * 기본설정, 가격/재고, 상세페이지설정, 주문내역 4개 메뉴에서 사용
 * 
 * @param {string} productName - 상품명
 * @param {string|number} productId - 상품번호
 * @param {string} currentPage - 현재 페이지 ('basic' | 'price' | 'detail' | 'orders')
 * @param {function} onProductNameClick - 상품명 클릭 시 (ProductSwitcher 열기)
 * @param {function} onBasicSettingsClick - 기본설정 클릭 시
 * @param {React.ReactNode} rightActions - 우측 액션 버튼들 (인쇄, 일괄업로드 등)
 * @param {string} productType - 상품 타입 ('tour' | 'hotel' | 'general')
 * @param {boolean} showBackButton - 돌아가기 버튼 표시 여부
 * @param {function} onBackClick - 돌아가기 클릭 시
 */
const ProductPageHeader = ({
  productName,
  productId,
  currentPage,
  onProductNameClick,
  onBasicSettingsClick,
  rightActions,
  productIdRight,
  productType = 'tour',
  showBackButton = true,
  onBackClick,
}) => {
  // 메뉴 아이템 정의
  const menuItems = [
    {
      key: 'basic',
      label: '기본설정',
      icon: (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      onClick: onBasicSettingsClick,
    },
    {
      key: 'price',
      label: '가격/재고',
      icon: (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      to: productType === 'general' 
        ? `/general/price-stock/${productId}`
        : `/price-stock/${productId}`,
    },
    {
      key: 'detail',
      label: '상세페이지',
      icon: (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      to: productType === 'general'
        ? `/general/detail-settings/${productId}`
        : `/detail-settings/${productId}`,
    },
    ...(productType === 'tour'
      ? [
          {
            key: 'salesCondition',
            label: '판매조건 설정',
            icon: (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z"
                />
              </svg>
            ),
            to: `/sales-condition/${productId}`,
          },
        ]
      : []),
    {
      key: 'orders',
      label: '주문내역',
      icon: (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
      to: productType === 'general'
        ? '/general/orders/-'
        : '/orders/-',
      state: { productName, productNo: productId },
    },
  ];

  return (
    <div className="sticky top-0 z-30 bg-gradient-to-r from-slate-50 via-white to-slate-50 border rounded-xl">
      {/* 상단 행: 상품 정보 + 우측 액션 */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          {/* 좌측: 상품 정보 */}
          <div className="flex items-center gap-4 min-w-0">
            {/* 상품 아이콘 */}
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
              {productType === 'hotel' ? (
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              ) : productType === 'general' ? (
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z" />
                </svg>
              )}
            </div>
            
            {/* 상품명 + 상품번호 */}
            <div className="min-w-0">
              <button
                type="button"
                onClick={onProductNameClick}
                className="group flex items-center gap-2 text-left"
                title="다른 상품으로 이동"
              >
                <h1 className="text-lg font-bold text-gray-900 truncate max-w-[600px] group-hover:text-blue-600 transition-colors">
                  {productName || '상품명 없음'}
                </h1>
                <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                </svg>
              </button>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium text-slate-600">
                  상품번호 #{productId}
                  
                </span>
                {productIdRight}
                <span className="text-xs text-slate-500">
                  {productType === 'hotel' ? '호텔' : productType === 'general' ? '일반상품' : '투어'}
                </span>
              </div>
            </div>
          </div>

          {/* 우측: 액션 버튼들 */}
          <div className="flex items-center gap-2">
          
            {rightActions}
          </div>
        </div>
      </div>

      {/* 하단 행: 네비게이션 탭 */}
      <div className="px-4 pb-2">
        <nav className="flex items-center gap-1">
          {menuItems.map((item) => {
            const isActive = currentPage === item.key;
            const isDisabled = isActive;
            
            const baseClasses = "inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-all";
            const activeClasses = "bg-blue-600 text-white shadow-md shadow-blue-200";
            const inactiveClasses = "text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:border-slate-300 hover:shadow-sm";
            
            const content = (
              <>
                {item.icon}
                <span>{item.label}</span>
              </>
            );

            if (item.onClick) {
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={item.onClick}
                  disabled={isDisabled}
                  className={`${baseClasses} ${isActive ? activeClasses : inactiveClasses}`}
                >
                  {content}
                </button>
              );
            }

            if (isActive) {
              return (
                <button
                  key={item.key}
                  type="button"
                  disabled
                  className={`${baseClasses} ${activeClasses}`}
                >
                  {content}
                </button>
              );
            }

            return (
              <Link
                key={item.key}
                to={item.to}
                state={item.state}
                className={`${baseClasses} ${inactiveClasses}`}
              >
                {content}
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default ProductPageHeader;
